import type { SourcePlatform } from '~entities/import-job/api/types';

/** When multiple source columns map to one Enrollio field, pick the preferred source. */
export const ALIAS_SOURCE_PRIORITY: Partial<
  Record<SourcePlatform, Partial<Record<string, string[]>>>
> = {
  jackrabbit: {
    'Parent Primary Phone': ['Customer Phone', 'Parent Cell', 'Account Home Phone'],
    'Parent Email': ['Customer Email', 'Email', 'Primary Email'],
    'Parent Last Name': ['Last Name'],
    'Parent First Name': ['First Name', 'Customer'],
  },
  studio_pro: {
    'Parent Email': ['Parent Email', 'Email1'],
    'Parent Primary Phone': ['Parent Phone', 'CellPhone'],
    'Parent First Name': ['Parent', 'ParentFirst'],
    'Parent Last Name': ['ParentLast'],
    'Student 1 First Name': ['Student Name', 'StudentFirst'],
    'Student 1 Last Name': ['StudentLast'],
    'Student 1 Email': ['Student Email', 'Student'],
  },
  /** Legacy id — same priority as Studio Pro. */
  dsp: {
    'Parent Email': ['Parent Email', 'Email1'],
    'Parent Primary Phone': ['Parent Phone', 'CellPhone'],
    'Parent First Name': ['Parent', 'ParentFirst'],
    'Parent Last Name': ['ParentLast'],
    'Student 1 First Name': ['Student Name', 'StudentFirst'],
    'Student 1 Last Name': ['StudentLast'],
    'Student 1 Email': ['Student Email', 'Student'],
  },
  studio_director: {
    'Parent First Name': ['Guardian'],
    'Parent Last Name': ['Family Last Name'],
    'Parent Email': ['Email'],
    'Parent Primary Phone': ['Phone1'],
    'Address Street': ['Address1'],
    'Address Apartment': ['Address2'],
    'Student 1 First Name': ['Student 1 Name'],
    'Student 1 Date of Birth': ['Student 1 DOB'],
  },
};

function normalizeHeaderKey(header: string): string {
  return header.replace(/^\uFEFF/i, '').trim();
}

function headerLookup(headers: string[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const header of headers) {
    const normalized = normalizeHeaderKey(header);
    if (!normalized) continue;
    map.set(normalized.toLowerCase(), normalized);
  }
  return map;
}

export function resolvePreMappedColumn(
  enrollioField: string,
  sourceHeaders: string[],
  aliases: Record<string, string>,
  sourcePlatform?: SourcePlatform,
): string {
  const normalizedHeaders = sourceHeaders.map(normalizeHeaderKey).filter(Boolean);
  const headersByLower = headerLookup(normalizedHeaders);

  const direct = headersByLower.get(enrollioField.toLowerCase());
  if (direct) {
    return direct;
  }

  const aliasTargetBySourceLower = new Map<string, string>();
  for (const [source, target] of Object.entries(aliases)) {
    const key = normalizeHeaderKey(source).toLowerCase();
    if (key) aliasTargetBySourceLower.set(key, target);
  }

  const matches: string[] = [];
  for (const header of normalizedHeaders) {
    const target = aliasTargetBySourceLower.get(header.toLowerCase());
    if (target?.toLowerCase() === enrollioField.toLowerCase()) {
      matches.push(header);
    }
  }

  if (matches.length === 0) {
    return '';
  }
  if (matches.length === 1) {
    return matches[0] ?? '';
  }

  const priority =
    sourcePlatform != null
      ? ALIAS_SOURCE_PRIORITY[sourcePlatform]?.[enrollioField]
      : undefined;
  if (priority) {
    for (const preferred of priority) {
      const match = headersByLower.get(preferred.toLowerCase());
      if (match && matches.includes(match)) {
        return match;
      }
    }
  }

  return matches[0] ?? '';
}
